import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./ProfilePage.css";
import Navbar from "./Navbar";
import ProfileCard from "./ProfileCard";
import BookingList from "./BookingList";
import MyOrders from "./MyOrders";
import MyVouchers from "./MyVouchers";
import { supabase } from "../supabase";
import imageCompression from "browser-image-compression";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import {
  REFUND_WINDOW_HOURS,
  refundWindowHours,
  refundWindowMessage,
  hoursUntilBookingMYT,
  coachingGroupLabelDefault,
} from "../constants";

// Add parseBookingDateTime function
const parseBookingDateTime = (date, time) => {
  const [day, month, year] = date.split("/").map(Number);
  let [hourStr, minuteStr, period] = time.split(/[: ]/);
  let hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  if (period === "AM") {
    if (hour === 12) hour = 0;
  } else if (period === "PM") {
    if (hour !== 12) hour += 12;
  }
  return new Date(year, month - 1, day, hour, minute);
};

// Format a "YYYY-MM-DD" activity date for display without timezone drift.
const formatActivityDateStr = (s) => {
  const [y, m, d] = (s || "").split("-").map(Number);
  if (!y) return s;
  return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString("en-MY", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
};

// Fetch the user's upcoming activity (social session) bookings.
const fetchUserActivities = async (userId) => {
  const { data, error } = await supabase
    .from("activity_participants")
    // sports (name) rides on activities_sport_fk: activities can be Pickle or Padel
    // since 2026-09-08 and the card shows which.
    .select("id, seats, status, activities!inner (id, name, activity_date, start_time, end_time, status, sports (name))")
    .eq("user_id", userId)
    .eq("status", "active");
  if (error) {
    console.error("ProfilePage.js: Error fetching activities:", error);
    return [];
  }
  const todayStr = new Date().toISOString().split("T")[0];
  return (data || [])
    .filter((r) => r.activities && r.activities.status === "open" && r.activities.activity_date >= todayStr)
    .map((r) => ({
      id: r.id,
      seats: r.seats,
      name: r.activities.name,
      // Null-safe: an activity whose sport row is missing/unreadable just gets no chip.
      sport: r.activities.sports?.name || null,
      date: r.activities.activity_date,
      start_time: r.activities.start_time,
      end_time: r.activities.end_time,
    }))
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
};

// Moved fetchUserBookings outside the component
const fetchUserBookings = async (userId) => {
  const { data: bookingsData, error: bookingsError } = await supabase
    .from("bookings")
    .select(`
      id,
      court_id,
      user_id,
      booking_date,
      timeslot,
      booking_type,
      order_id,
      created_at,
      courts (id, name),
      program_schedule_instance_participants (
        instance_id,
        program_schedule_instances (
          schedule_id,
          program_schedules (
            program_id,
            court_id,
            programs (
              name,
              coach,
              sport_id,
              level,
              sports (
                name
              )
            )
          )
        )
      )
    `)
    .eq("user_id", userId)
    .in("booking_type", ["court_booking", "group_lesson"])
    .gte("booking_date", new Date().toISOString().split("T")[0]);

  if (bookingsError) {
    console.error("ProfilePage.js: Error fetching bookings:", bookingsError);
    return [];
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [time, period] = timeStr.split(" ");
    const [hour, minute] = time.split(":").map(Number);
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const inferSport = (name) => {
    if (!name) return "Unknown";
    const lowerName = name.toLowerCase();
    if (lowerName.includes("pickleball") || lowerName.includes("pickle"))
      return "Pickle";
    if (lowerName.includes("padel")) return "Padel";
    if (lowerName.includes("tournament") || lowerName.includes("show"))
      return "Pickle";
    return "Unknown";
  };

  const formatCourtName = (courtName) => {
    if (!courtName) return "Unknown Court";
    const lowerName = courtName.toLowerCase();
    if (
      lowerName.includes("tournament court") ||
      lowerName.includes("show court")
    ) {
      return `Pickle ${courtName}`;
    }
    return courtName;
  };

  const formattedCourtBookings = bookingsData
    .filter((booking) => booking.booking_type === "court_booking")
    .map((booking) => {
      const courtName = booking.courts?.name || "Unknown Court";
      const formattedName = formatCourtName(courtName);
      const sport = inferSport(courtName);
      return {
        id: booking.id,
        type: "Bookings",
        name: formattedName,
        date: formatDate(booking.booking_date),
        time: formatTime(booking.timeslot),
        sport: sport,
        created_at: booking.created_at,
        order_id: booking.order_id,
      };
    });

  const formattedGroupLessons = bookingsData
    .filter((booking) => booking.booking_type === "group_lesson")
    .map((booking) => {
      const participantEntry =
        booking.program_schedule_instance_participants &&
        booking.program_schedule_instance_participants.length > 0
          ? booking.program_schedule_instance_participants[0]
          : null;
      const instance = participantEntry?.program_schedule_instances || {};
      const schedule = instance?.program_schedules || {};
      const programDetails = schedule?.programs || {};
      const courtName = booking.courts?.name || "Unknown Court";
      const sportName = inferSport(courtName) || "Unknown";
      const formattedCourtName = formatCourtName(courtName);
      const formattedLessonName = programDetails.name
        ? `${programDetails.name} on ${formattedCourtName}`
        : `Group Lesson on ${formattedCourtName}`;
      return {
        id: booking.id,
        type: "Lessons",
        lessonType: "Group",
        name: formattedLessonName,
        date: formatDate(booking.booking_date),
        time: formatTime(booking.timeslot),
        coach: programDetails.coach || undefined,
        sport: sportName,
        level: programDetails.level || "Unknown",
        created_at: booking.created_at,
        order_id: booking.order_id,
      };
    });

  const combinedBookings = [
    ...formattedCourtBookings,
    ...formattedGroupLessons,
  ].sort((a, b) => {
    const dateA = parseBookingDateTime(a.date, a.time);
    const dateB = parseBookingDateTime(b.date, b.time);
    return dateA - dateB;
  });

  return combinedBookings;
};

const fetchCoachingBookings = async (userId) => {
  const { data: bookingsData, error: bookingsError } = await supabase
    .from("bookings")
    .select(`
      id,
      court_id,
      user_id,
      booking_date,
      timeslot,
      booking_type,
      order_id,
      created_at,
      courts (id, name, court_types (sports (name))),
      coach_bookings (
        id,
        coach_id,
        pax,
        profiles:coach_id (
          display_name,
          username
        )
      )
    `)
    .eq("user_id", userId)
    .eq("booking_type", "private_lesson")
    .gte("booking_date", new Date().toISOString().split("T")[0]);

  if (bookingsError) {
    console.error("ProfilePage.js: Error fetching coaching bookings:", bookingsError);
    return [];
  }

  // Fetch coach_sports display names for all coaches in results
  const coachIds = [...new Set((bookingsData || [])
    .map(b => b.coach_bookings?.[0]?.coach_id)
    .filter(Boolean))];

  let coachSportsMap = {};
  if (coachIds.length > 0) {
    const { data: coachSportsData } = await supabase
      .from("coach_sports")
      .select("coach_id, display_name")
      .in("coach_id", coachIds);
    for (const cs of (coachSportsData || [])) {
      if (cs.display_name) {
        coachSportsMap[cs.coach_id] = cs.display_name;
      }
    }
  }

  // class_type ('group' | 'private') lives in the ORDER's booking_metadata, not on
  // the booking row, so it takes a second lookup. Needed here because a group class
  // is coach-cancel-only. Fails OPEN by design: if this comes back empty the session
  // reads as private and delete-coaching-booking still refuses the call with the same
  // reason — worst case is a click and a message instead of a pre-greyed button.
  const orderIds = [...new Set((bookingsData || []).map(b => b.order_id).filter(Boolean))];
  let orderClassTypeMap = {};
  if (orderIds.length > 0) {
    const { data: ordersData } = await supabase
      .from("orders")
      .select("order_id, booking_metadata")
      .in("order_id", orderIds);
    for (const o of (ordersData || [])) {
      if (o.booking_metadata?.class_type) {
        orderClassTypeMap[o.order_id] = o.booking_metadata.class_type;
      }
    }
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [time, period] = timeStr.split(" ");
    const [hour, minute] = time.split(":").map(Number);
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const inferSport = (courtName) => {
    if (!courtName) return "Unknown";
    const lowerName = courtName.toLowerCase();
    if (lowerName.includes("pickleball") || lowerName.includes("pickle")) return "Pickle";
    if (lowerName.includes("tournament") || lowerName.includes("show")) return "Pickle";
    if (lowerName.includes("padel")) return "Padel";
    return "Unknown";
  };

  return (bookingsData || []).map((booking) => {
    const coachBooking = booking.coach_bookings?.[0];
    const coachProfile = coachBooking?.profiles;
    const coachName = coachSportsMap[coachBooking?.coach_id] || coachProfile?.display_name || coachProfile?.username || "Coach";
    const pax = coachBooking?.pax || 1;
    const courtName = booking.courts?.name || "Unknown Court";
    // Prefer the real sport from court -> court_type -> sport; fall back to name heuristic.
    const sport = booking.courts?.court_types?.sports?.name || inferSport(courtName);
    const isGroupClass = orderClassTypeMap[booking.order_id] === "group";
    // Sport DEFAULT label only ("Match Coach" for Padel, "Group Class" otherwise) —
    // the coach's custom coach_sports.group_label is deliberately not read here, to
    // keep this list off a column the coach-name query would break on if the
    // add_coach_group_label migration is not applied yet.
    const classLabel = isGroupClass ? coachingGroupLabelDefault(sport) : "Coaching";

    return {
      id: booking.id,
      coach_booking_id: coachBooking?.id,
      type: "Coaching",
      isGroupClass,
      name: `${sport} ${classLabel} with ${coachName}`,
      date: formatDate(booking.booking_date),
      time: formatTime(booking.timeslot),
      coach: coachName,
      sport,
      pax,
      courtName,
      created_at: booking.created_at,
      order_id: booking.order_id,
    };
  }).sort((a, b) => {
    const dateA = parseBookingDateTime(a.date, a.time);
    const dateB = parseBookingDateTime(b.date, b.time);
    return dateA - dateB;
  });
};

const SESSION_DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SESSION_MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// Turns a "DD/MM/YYYY" date + "HH:MM AM" start time into "Wednesday, May 28, 6:00 AM - 7:00 AM".
const formatSessionSchedule = (date, time) => {
  const [day, month, year] = date.split("/").map(Number);
  const dateObj = new Date(year, month - 1, day);
  const dayOfWeek = SESSION_DAYS[dateObj.getDay()];
  const monthName = SESSION_MONTHS[dateObj.getMonth()];

  const [t, period] = time.split(" ");
  const [hourStr, minuteStr] = t.split(":");
  let startHour = parseInt(hourStr);
  if (period === "PM" && startHour !== 12) startHour += 12;
  if (period === "AM" && startHour === 12) startHour = 0;
  const endHour = startHour + 1;
  const endPeriod = endHour >= 12 ? "PM" : "AM";
  const endHour12 = endHour % 12 || 12;
  const timeRange = `${time} - ${endHour12}:${minuteStr} ${endPeriod}`;

  return `${dayOfWeek}, ${monthName} ${dateObj.getDate()}, ${timeRange}`;
};

// Sessions where the logged-in user is the assigned coach (read-only view).
// The RPC derives the coach from the JWT, so no coach id is passed from the client.
const fetchCoachSessions = async () => {
  const { data, error } = await supabase.rpc("get_coach_sessions");

  if (error) {
    console.error("ProfilePage.js: Error fetching coach sessions:", error);
    return [];
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [time, period] = timeStr.split(" ");
    const [hour, minute] = time.split(":").map(Number);
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const inferSport = (courtName) => {
    if (!courtName) return "Unknown";
    const lowerName = courtName.toLowerCase();
    if (lowerName.includes("pickleball") || lowerName.includes("pickle")) return "Pickle";
    if (lowerName.includes("tournament") || lowerName.includes("show")) return "Pickle";
    if (lowerName.includes("padel")) return "Padel";
    return "Unknown";
  };

  return (data || []).map((row) => {
    const courtName = row.court_name || "Unknown Court";
    return {
      id: row.booking_id,
      coach_booking_id: row.coach_booking_id,
      customerName: row.customer_name || row.customer_username || "Player",
      customerEmail: row.customer_email || "",
      pax: row.pax || 1,
      date: formatDate(row.booking_date),
      time: formatTime(row.timeslot),
      // Prefer the real sport from the RPC (court -> court_type -> sport); fall back to heuristic.
      sport: row.sport_name || inferSport(courtName),
      courtName,
    };
  }).sort((a, b) => {
    const dateA = parseBookingDateTime(a.date, a.time);
    const dateB = parseBookingDateTime(b.date, b.time);
    return dateA - dateB;
  });
};

// Notification Modal Component
const NotificationModal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Notification</h2>
        <p>{message}</p>
        <button className="modal-close-button" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} /> Got It
        </button>
      </div>
    </div>
  );
};

const ProfilePage = ({ onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedSport, setSelectedSport] = useState(null);
  const [selectedBookingType, setSelectedBookingType] = useState(null);
  const [isProfileActive, setIsProfileActive] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentUserBookings, setCurrentUserBookings] = useState([]);
  const [coachingBookings, setCoachingBookings] = useState([]);
  const [coachSessions, setCoachSessions] = useState([]);
  const [activityBookings, setActivityBookings] = useState([]);
  const [isPaymentPending, setIsPaymentPending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refundEligibility, setRefundEligibility] = useState({});
  const [cancelWindowHours, setCancelWindowHours] = useState(REFUND_WINDOW_HOURS);
  const [coachingRefundEligibility, setCoachingRefundEligibility] = useState({});
  const [cancellingCoachingId, setCancellingCoachingId] = useState(null);
  const [notification, setNotification] = useState({ message: "", show: false });
  // Bumped after a successful cancellation so the My Orders section re-fetches
  // and shows the new order/add-on refund state.
  const [ordersRefreshToken, setOrdersRefreshToken] = useState(0);
  // Purely presentational: which section of the page is showing. All the data
  // fetched above keeps loading regardless of the active tab — this only
  // decides what gets rendered. Defaults to "bookings" since that is the
  // most-used section.
  const [activeTab, setActiveTab] = useState("bookings");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("status") === "pending") {
      setIsPaymentPending(true);
    }
  }, [location]);

  const formatMembershipStatus = (status) => {
    switch (status) {
      case "normal_member":
        return "Normal Member";
      case "athlete":
        return "Athlete";
      case "coach":
        return "Coach";
      case "super_host":
        return "Super Host";
      case "super_host_pickle":
        return "Pickleball Super Host";
      case "super_host_padel":
        return "Padel Super Host";
      case "long_term_member":
        return "Long-term Member";
      case "vip_maserati":
        return "Maserati Owners";
      default:
        return "Normal Member";
    }
  };

  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    gender: "",
    date_of_birth: "",
    avatarUrl: "",
    status: "Normal Member",
  });

  const encodeUsername = (username) => {
    if (!username || !username.includes("_")) return username || "Unknown User";
    const [, userId] = username.split("_");
    try {
      const encodedId = btoa(userId).replace(/=/g, "").slice(0, 8);
      return `user_${encodedId}`;
    } catch (error) {
      console.error("ProfilePage.js: Error encoding username:", error);
      return username;
    }
  };

  useEffect(() => {
    const checkSessionAndFetchData = async () => {
      setIsLoading(true);
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session) {
        if (location.pathname !== "/login") navigate("/login");
        setIsLoading(false);
        return;
      }

      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();
      if (userError || !user) {
        if (location.pathname !== "/login") navigate("/login");
        setIsLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", user.id)
        .single();

      if (profileError) {
        setProfileData((prev) => ({
          ...prev,
          name: "Error Loading Profile: Profile Not Found",
        }));
        navigate("/setup-profile");
        setIsLoading(false);
        return;
      }

      if (profile) {
        setProfileData((prev) => ({
          ...prev,
          name: encodeUsername(profile.username),
          email: user.email || "",
          gender: profile.gender || "",
          date_of_birth: profile.date_of_birth || "",
          avatarUrl: profile.avatar_url || prev.avatarUrl,
          status: formatMembershipStatus(profile.membership_status),
        }));
      }

      const bookings = await fetchUserBookings(user.id);
      setCurrentUserBookings(bookings);

      const coaching = await fetchCoachingBookings(user.id);
      setCoachingBookings(coaching);

      const activities = await fetchUserActivities(user.id);
      setActivityBookings(activities);

      // Coach view: a user is a coach if they have a coach_sports row or the 'coach' membership.
      const { data: coachSportRows } = await supabase
        .from("coach_sports")
        .select("coach_id")
        .eq("coach_id", user.id)
        .limit(1);
      // The short cancellation window keys off coach_sports ALONE — a teaching
      // coach — while the coach session panel below keeps the wider membership-
      // inclusive test. Deliberately two predicates: process-refund-as-credits-v7
      // grants the 6h window on coach_sports only, and this must match the server
      // that answers the call or the button enables a request it then refuses.
      const hasCoachSports = !!(coachSportRows && coachSportRows.length > 0);
      const userIsCoach =
        hasCoachSports ||
        profile?.membership_status === "coach";
      if (userIsCoach) {
        const sessions = await fetchCoachSessions();
        setCoachSessions(sessions);
      }

      // Teaching coaches may cancel court bookings and group lessons up to 6
      // hours before the start instead of 48. Mirrors process-refund-as-credits-v7,
      // which re-derives this server-side — this value only drives the button state.
      const windowHours = refundWindowHours(hasCoachSports);
      setCancelWindowHours(windowHours);

      // Pre-compute refund eligibility
      const eligibility = {};
      for (const booking of bookings) {
        if (!booking.order_id) {
          // Free booking: always eligible for cancellation
          eligibility[booking.id] = {
            eligible: true,
            message: "This is a free booking and can be canceled.",
          };
        } else {
          // Paid booking
          const { data: order, error } = await supabase
            .from("orders")
            .select("status, total_amount, refunded_amount")
            .eq("order_id", booking.order_id)
            .single();

          if (error || !order) {
            eligibility[booking.id] = {
              eligible: false,
              message: "Unable to check refund status.",
            };
          } else {
            const { status: orderStatus, total_amount, refunded_amount = 0 } = order;
            const hoursUntilBooking = hoursUntilBookingMYT(
              booking.date,
              booking.time
            );

            if (orderStatus === "fully_refunded") {
              eligibility[booking.id] = {
                eligible: false,
                message: "Order has been fully refunded.",
              };
            } else if (orderStatus === "refund_pending") {
              eligibility[booking.id] = {
                eligible: false,
                message: "A refund for this order is already pending.",
              };
            } else if (hoursUntilBooking <= windowHours) {
              eligibility[booking.id] = {
                eligible: false,
                message: refundWindowMessage(windowHours),
              };
            } else {
              eligibility[booking.id] = {
                eligible: true,
                message: "This booking is eligible for a refund. The exact amount will be calculated upon refund.",
              };
            }
          }
        }
      }
      setRefundEligibility(eligibility);

      // Pre-compute coaching refund eligibility
      const coachingEligibility = {};
      for (const session of coaching) {
        // Anchored to +08:00 like every other gate on this page. `nextDayEarlyAM:
        // false` is deliberate: this gate's server is delete-coaching-booking,
        // which parses `${booking_date}T${hh:mm}+08:00` with NO next-day shift,
        // and the client must agree with the endpoint that answers the call.
        // Coaching stays at 48h for everyone — the coach exception is scoped to
        // court bookings and group lessons (process-refund-as-credits-v7).
        const hoursUntilBooking = hoursUntilBookingMYT(session.date, session.time, {
          nextDayEarlyAM: false,
        });

        if (session.isGroupClass) {
          // Coach-cancel-only, at any notice — the coach runs the block for 3–4
          // players, so a student leaving is not theirs to decide. Checked BEFORE
          // the 48h window so the message names the real reason, and mirrored by
          // delete-coaching-booking's GROUP_CANCEL_COACH_ONLY (the authority).
          // The coach's own panel entry is a separate list item and is unaffected.
          coachingEligibility[session.id] = {
            eligible: false,
            message:
              "Group classes can only be cancelled by your coach. Please contact your coach to cancel this session.",
          };
        } else if (hoursUntilBooking <= 48) {
          coachingEligibility[session.id] = {
            eligible: false,
            message: "Cannot cancel within 48 hours of the session.",
          };
        } else {
          coachingEligibility[session.id] = {
            eligible: true,
            message: "This session can be cancelled — its cost is refunded as credits.",
          };
        }
      }
      setCoachingRefundEligibility(coachingEligibility);
      setIsLoading(false);
    };

    checkSessionAndFetchData();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (
          event === "SIGNED_OUT" ||
          (!session && event === "INITIAL_SESSION")
        ) {
          if (location.pathname !== "/login") navigate("/login");
        } else if (event === "SIGNED_IN") {
          checkSessionAndFetchData();
        }
      }
    );

    return () => authListener.subscription.unsubscribe();
  }, [navigate, location]);

  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date(); // Dynamically uses the current date (June 08, 2025, 12:18 PM +08)
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const validateUsername = (username) => {
    const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/;
    return usernameRegex.test(username)
      ? null
      : "Username must be 3-20 characters long and contain only letters, numbers, and underscores.";
  };

  const validateDateOfBirth = (dob) => {
    if (!dob) return null;
    const age = calculateAge(dob);
    return age < 13 ? "You must be at least 13 years old." : null;
  };

  const handleCloseNotification = () => {
    setNotification({ message: "", show: false });
  };

  const handleSaveProfile = async (updatedData) => {
    const { name, gender, date_of_birth, avatarFile, avatarUrl } = updatedData;

    const usernameError = validateUsername(name);
    if (usernameError) {
      setNotification({ message: usernameError, show: true });
      return false;
    }

    const dobError = validateDateOfBirth(date_of_birth);
    if (dobError) {
      setNotification({ message: dobError, show: true });
      return false;
    }

    let finalAvatarUrl = avatarUrl;
    let oldAvatarPath = null;

    if (avatarFile) {
      const maxSizeMB = 5;
      if (avatarFile.size > maxSizeMB * 1024 * 1024) {
        setNotification({
          message: `File size exceeds ${maxSizeMB}MB limit. Please upload a smaller image.`,
          show: true,
        });
        return false;
      }

      try {
        const options = {
          maxSizeMB: 0.2,
          maxWidthOrHeight: 512,
          useWebWorker: true,
        };
        const compressedFile = await imageCompression(avatarFile, options);
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const fileName = `${user.id}_${Date.now()}.${compressedFile.name
          .split(".")
          .pop()}`;

        if (avatarUrl && avatarUrl.includes("/avatars/")) {
          const urlParts = avatarUrl.split("/avatars/");
          if (urlParts[1]) oldAvatarPath = urlParts[1];
        }

        const { error: uploadError } = await supabase.storage
          .from("avatars")
          .upload(fileName, compressedFile, {
            cacheControl: "3600",
            upsert: false,
          });

        if (uploadError) {
          console.error("ProfilePage.js: Error uploading image:", uploadError);
          setNotification({
            message: "Failed to upload image. Please try again.",
            show: true,
          });
          return false;
        }

        const { data: publicUrlData } = supabase.storage
          .from("avatars")
          .getPublicUrl(fileName);
        finalAvatarUrl = publicUrlData.publicUrl;

        if (oldAvatarPath) {
          const { error: deleteError } = await supabase.storage
            .from("avatars")
            .remove([oldAvatarPath]);
          if (deleteError)
            console.error(
              "ProfilePage.js: Error deleting old avatar:",
              deleteError
            );
        }
      } catch (error) {
        console.error(
          "ProfilePage.js: Error compressing or uploading image:",
          error
        );
        setNotification({
          message: "An error occurred while processing the image. Please try again.",
          show: true,
        });
        return false;
      }
    }

    const { data: updateProfileResponse, error: updateProfileError } = await supabase.functions.invoke("client-update-profile", {
      body: { name, gender, date_of_birth, final_avatar_url: finalAvatarUrl},
    });
    // const { error } = await supabase
    //   .from("profiles")
    //   .update({
    //     username: name,
    //     gender,
    //     date_of_birth,
    //     avatar_url: finalAvatarUrl,
    //   })
    //   .eq("user_id", user.id);

    if (updateProfileError) {
      console.error("ProfilePage.js: Error updating profile:", updateProfileError);
      if (updateProfileError.code === "23505") {
        setNotification({
          message: "This username is already taken. Please choose a different one.",
          show: true,
        });
      } else {
        setNotification({
          message: "Failed to save profile changes. Please try again.",
          show: true,
        });
      }
      return false;
    }

    setProfileData((prev) => ({
      ...prev,
      name: encodeUsername(name),
      gender,
      date_of_birth,
      avatarUrl: finalAvatarUrl,
    }));
    setIsEditing(false);
    return true;
  };

  const handleBookingTypeSelect = (type) => {
    setSelectedBookingType(type);
    setSelectedSport(null);
    setIsProfileActive(false);
    navigate("/");
  };

  const handleProfileClick = () => {};

  const handleCancelCoaching = async (session, cancelKey = session.id) => {
    const eligibility = coachingRefundEligibility[session.id];
    if (!eligibility || !eligibility.eligible) {
      setNotification({
        message: eligibility?.message || "Unable to cancel this session.",
        show: true,
      });
      return;
    }

    if (!session.coach_booking_id) {
      setNotification({
        message: "Unable to find coaching booking record. Please contact support.",
        show: true,
      });
      return;
    }

    const confirmCancel = window.confirm(
      "Are you sure you want to cancel this coaching session? This session's cost will be refunded as credits to your account."
    );
    if (!confirmCancel) return;

    setCancellingCoachingId(cancelKey);
    try {
      const { data, error } = await supabase.functions.invoke("delete-coaching-booking", {
        body: { coach_booking_id: session.coach_booking_id },
      });

      if (error) {
        console.error("ProfilePage.js: Error cancelling coaching:", error);
        let errorMessage = "Failed to cancel coaching session.";
        try {
          if (error.context && error.context.json) {
            const body = await error.context.json();
            errorMessage = body?.error || errorMessage;
          } else {
            errorMessage = error.message || errorMessage;
          }
        } catch (e) {
          errorMessage = error.message || errorMessage;
        }
        setNotification({
          message: errorMessage,
          show: true,
        });
        setCancellingCoachingId(null);
        return;
      }

      if (data && data.error) {
        setNotification({
          message: data.error,
          show: true,
        });
        setCancellingCoachingId(null);
        return;
      }

      // Refresh coaching bookings
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const updatedCoaching = await fetchCoachingBookings(user.id);
        setCoachingBookings(updatedCoaching);
        const updatedEligibility = { ...coachingRefundEligibility };
        delete updatedEligibility[session.id];
        setCoachingRefundEligibility(updatedEligibility);
      }
      // The coaching order's status/refund just changed — My Orders shows it.
      setOrdersRefreshToken((token) => token + 1);

      setNotification({
        message: data?.message || "Coaching session cancelled successfully. Credits have been added to your account.",
        show: true,
      });
    } catch (error) {
      console.error("ProfilePage.js: Unexpected error cancelling coaching:", error);
      setNotification({
        message: "An unexpected error occurred. Please try again.",
        show: true,
      });
    } finally {
      setCancellingCoachingId(null);
    }
  };

  // Coach-initiated cancellation: a coach cancels a session booked with them.
  // The customer is refunded this session's cost as credits (no 48h restriction for the coach).
  const handleCoachCancelSession = async (session, cancelKey = session.id) => {
    if (!session.coach_booking_id) {
      setNotification({
        message: "Unable to find coaching booking record. Please contact support.",
        show: true,
      });
      return;
    }

    const confirmCancel = window.confirm(
      "Cancel this coaching session? The customer will be refunded this session's cost as credits."
    );
    if (!confirmCancel) return;

    setCancellingCoachingId(cancelKey);
    try {
      const { data, error } = await supabase.functions.invoke("delete-coaching-booking", {
        body: { coach_booking_id: session.coach_booking_id },
      });

      if (error) {
        console.error("ProfilePage.js: Error cancelling coach session:", error);
        let errorMessage = "Failed to cancel coaching session.";
        try {
          if (error.context && error.context.json) {
            const body = await error.context.json();
            errorMessage = body?.error || errorMessage;
          } else {
            errorMessage = error.message || errorMessage;
          }
        } catch (e) {
          errorMessage = error.message || errorMessage;
        }
        setNotification({ message: errorMessage, show: true });
        setCancellingCoachingId(null);
        return;
      }

      if (data && data.error) {
        setNotification({ message: data.error, show: true });
        setCancellingCoachingId(null);
        return;
      }

      // Refresh the coach's session list
      const sessions = await fetchCoachSessions();
      setCoachSessions(sessions);
      // If the coach cancelled a session they booked as their own customer,
      // that order appears in their My Orders too.
      setOrdersRefreshToken((token) => token + 1);

      setNotification({
        message: data?.message || "Coaching session cancelled. The customer has been refunded as credits.",
        show: true,
      });
    } catch (err) {
      console.error("ProfilePage.js: Unexpected error cancelling coach session:", err);
      setNotification({
        message: "An unexpected error occurred. Please try again.",
        show: true,
      });
    } finally {
      setCancellingCoachingId(null);
    }
  };

  const handleAddToGoogleCalendar = (booking) => {
    const startDateTime = parseBookingDateTime(booking.date, booking.time);
    const endDateTime = new Date(startDateTime.getTime() + 60 * 60 * 1000);

    let eventTitle;
    if (booking.type === "Bookings") {
      eventTitle = `PickleDepot: Court Booking - ${booking.name}`;
    } else if (booking.type === "Lessons") {
      eventTitle = `PickleDepot: Group Lesson - ${booking.sport} ${booking.name} (${booking.level})`;
      if (booking.coach) eventTitle += ` with ${booking.coach}`;
    }

    const event = {
      text: eventTitle,
      start: startDateTime.toISOString().replace(/-|:|\.\d\d\d/g, ""),
      end: endDateTime.toISOString().replace(/-|:|\.\d\d\d/g, ""),
      details: booking.coach ? `Coach: ${booking.coach}` : "",
    };

    const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
      event.text
    )}&dates=${event.start}/${event.end}&details=${encodeURIComponent(
      event.details
    )}`;
    window.open(googleCalendarUrl, "_blank");
  };

  const handleRefund = async (bookingId) => {
    const booking = currentUserBookings.find((b) => b.id === bookingId);
    if (!booking) {
      setNotification({
        message: "Booking not found. Please refresh and try again.",
        show: true,
      });
      return { error: true, message: "Booking not found" };
    }

    // Bookings created by our team (admin) have no associated order. Users
    // can't self-cancel these — surface a clear message instead of failing
    // with a confusing "order not found" error from the refund function.
    if (!booking.order_id) {
      setNotification({
        message:
          "This booking was made by our team and can't be cancelled here. Please contact us to make any changes.",
        show: true,
      });
      return {
        error: true,
        message: "Admin-created bookings cannot be cancelled online.",
      };
    }

    // Paid booking: proceed with refund eligibility check
    const eligibility = refundEligibility[bookingId];
    if (!eligibility || !eligibility.eligible) {
      setNotification({
        message:
          eligibility?.message ||
          "Unable to process refund. Please try again or contact support.",
        show: true,
      });
      return {
        error: true,
        message: eligibility?.message || "Refund not eligible",
      };
    }

    try {
      const { data, error } = await supabase.functions.invoke(
        "process-refund-as-credits-v7",
        {
          body: JSON.stringify({ bookingId }),
        }
      );

      if (error) {
        // supabase-js puts the body of a non-2xx response in error.context, not
        // error.message — read it so the user sees the server's own wording
        // (which carries the cancellation window that actually applies to them).
        let errorMessage = error.message || "Unknown error";
        try {
          if (error.context && error.context.json) {
            const body = await error.context.json();
            errorMessage = body?.error || errorMessage;
          }
        } catch (e) {
          // Keep error.message as the fallback.
        }
        setNotification({
          message:
            `An error occurred: ${errorMessage}. Please try again or contact support if the issue persists.`,
          show: true,
        });
        return { error: true, message: errorMessage };
      }

      if (data && typeof data === "object") {
        // Refresh bookings list
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const updatedBookings = await fetchUserBookings(user.id);
          setCurrentUserBookings(updatedBookings);
          const updatedEligibility = { ...refundEligibility };
          delete updatedEligibility[bookingId];
          setRefundEligibility(updatedEligibility);
        }
        // Re-fetch My Orders so the refund shows up there immediately.
        setOrdersRefreshToken((token) => token + 1);

        let message = data.message || "Booking refunded successfully.";
        // A successful add-on refund is already covered by the server's own
        // message — only a failed-closed add-on refund needs surfacing.
        if (data.addOnRefundError != null) {
          message +=
            " Your add-on refund could not be processed automatically — please contact us and we will credit it manually.";
        }

        setNotification({ message, show: true });
        return { error: false, message };
      }

      setNotification({
        message: "Unexpected response from refund process. Please contact support.",
        show: true,
      });
      return { error: true, message: "Unexpected response" };
    } catch (error) {
      console.error("Error processing refund:", error);
      setNotification({
        message:
          "An unexpected error occurred while processing the refund. Please try again or contact support.",
        show: true,
      });
      return { error: true, message: error.message || "Unknown error" };
    }
  };

  if (isLoading) {
    return (
      <div className="loading-spinner-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  // Single coaching list: sessions you booked as a student ("attending") plus,
  // for coaches, sessions booked with you ("coaching"). Sorted by start time.
  const combinedCoachingSessions = [
    ...coachingBookings.map((s) => ({
      key: `attending-${s.id}`,
      role: "attending",
      sessionId: s.id,
      title: s.name,
      date: s.date,
      time: s.time,
      pax: s.pax,
      courtName: s.courtName,
      email: null,
      raw: s,
    })),
    ...coachSessions.map((s) => ({
      key: `coaching-${s.id}`,
      role: "coaching",
      sessionId: s.id,
      title: `${s.sport} Coaching · ${s.customerName}`,
      date: s.date,
      time: s.time,
      pax: s.pax,
      courtName: s.courtName,
      email: s.customerEmail,
      raw: s,
    })),
  ].sort((a, b) => parseBookingDateTime(a.date, a.time) - parseBookingDateTime(b.date, b.time));

  // Purely presentational tab bar config for the right column — order here
  // is also the order the buttons render in. "bookings" is first/default as
  // the most-used section. Profile is NOT a tab: it is a persistent left
  // column, unchanged from the original layout.
  const PROFILE_TABS = [
    { id: "bookings", label: "Bookings" },
    { id: "orders", label: "Orders" },
    { id: "vouchers", label: "Vouchers" },
  ];

  return (
    <div className="App">
      <Navbar
        selectedSport={selectedSport}
        selectedBookingType={selectedBookingType}
        onBookingTypeSelect={handleBookingTypeSelect}
        onProfileClick={handleProfileClick}
        isProfileActive={isProfileActive}
      />
      <div className="content">
        <div className="profile-booking-content">
          <div className="profile-page">
            <div className="profile-columns">
              <div className="profile-form-column-left-column">
                <div className="step-title">Profile</div>
                <ProfileCard
                  {...profileData}
                  age={calculateAge(profileData.date_of_birth)}
                  isEditing={isEditing}
                  onEditToggle={() => setIsEditing(!isEditing)}
                  onSave={handleSaveProfile}
                />
              </div>

              <div className="profile-form-column-right-column">
                <div className="profile-tabs" role="tablist" aria-label="Profile sections">
                  {PROFILE_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={activeTab === tab.id}
                      className={`profile-tab-button ${activeTab === tab.id ? "active" : ""}`}
                      onClick={() => setActiveTab(tab.id)}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <div className="profile-tab-panel">
              {activeTab === "bookings" && (
              <div>
                <div className="step-title">Upcoming Bookings</div>
                {isPaymentPending && (
                  <div className="waiting-payment-message">
                    Waiting for payment to confirm your bookings. Once
                    confirmed, your bookings will appear here.
                  </div>
                )}
                <BookingList
                  userBookings={currentUserBookings.filter(
                    (booking) => booking.type === "Bookings"
                  )}
                  userLessons={currentUserBookings.filter(
                    (booking) => booking.type === "Lessons"
                  )}
                  onAddToGoogleCalendar={handleAddToGoogleCalendar}
                  onRefund={handleRefund}
                  refundEligibility={refundEligibility}
                  cancelWindowHours={cancelWindowHours}
                />

                <div className="step-title" style={{ marginTop: '24px' }}>Coaching Sessions</div>
                {combinedCoachingSessions.length === 0 ? (
                  <div className="no-items" style={{ padding: '12px 0', color: '#888' }}>No upcoming coaching sessions.</div>
                ) : (
                  <div className="coaching-sessions-list">
                    {combinedCoachingSessions.map((item) => {
                      const roleLabel = item.role === "coaching" ? "Coaching" : "Attending";

                      let actions = null;
                      if (item.role === "attending") {
                        const eligibility = coachingRefundEligibility[item.sessionId] || { eligible: false, message: "Unable to check cancellation status." };
                        const isCancelling = cancellingCoachingId === item.key;
                        actions = (
                          <div className="booking-actions">
                            <button
                              className={`action-button refund-button ${!eligibility.eligible ? "ineligible" : ""}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!eligibility.eligible) {
                                  setNotification({
                                    message: eligibility.message,
                                    show: true,
                                  });
                                  return;
                                }
                                handleCancelCoaching(item.raw, item.key);
                              }}
                              disabled={isCancelling}
                              title={eligibility.eligible ? "Cancel coaching session (refund as credits)" : eligibility.message}
                            >
                              {isCancelling ? "..." : <FontAwesomeIcon icon={faTimes} />}
                            </button>
                          </div>
                        );
                      } else if (item.role === "coaching") {
                        // Coach can cancel a session booked with them; customer is refunded this session's cost.
                        const isCancelling = cancellingCoachingId === item.key;
                        actions = (
                          <div className="booking-actions">
                            <button
                              className="action-button refund-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCoachCancelSession(item.raw, item.key);
                              }}
                              disabled={isCancelling}
                              title="Cancel this coaching session (refunds the customer as credits)"
                            >
                              {isCancelling ? "..." : <FontAwesomeIcon icon={faTimes} />}
                            </button>
                          </div>
                        );
                      }

                      return (
                        <div key={item.key} className="booking-card">
                          <div className="booking-content">
                            <div className="booking-title">
                              <span className={`session-role-badge ${item.role}`}>{roleLabel}</span>
                              {item.title}
                            </div>
                            <div className="booking-details">
                              {formatSessionSchedule(item.date, item.time)}
                            </div>
                            <div className="booking-details">
                              {item.pax} {item.pax === 1 ? "player" : "players"} · {item.courtName}
                            </div>
                            {item.email && (
                              <div className="booking-details">{item.email}</div>
                            )}
                          </div>
                          {actions}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="step-title" style={{ marginTop: '24px' }}>Upcoming Activities</div>
                {activityBookings.length === 0 ? (
                  <div className="no-items" style={{ padding: '12px 0', color: '#888' }}>No upcoming activities.</div>
                ) : (
                  <div className="coaching-sessions-list">
                    {activityBookings.map((a) => (
                      <div key={a.id} className="booking-card">
                        <div className="booking-content">
                          <div className="booking-title">
                            {a.sport && <span className="session-role-badge attending">{a.sport}</span>}
                            {a.name}
                          </div>
                          <div className="booking-details">
                            {formatActivityDateStr(a.date)} · {a.start_time} - {a.end_time}
                          </div>
                          <div className="booking-details">
                            {a.seats} {a.seats === 1 ? "seat" : "seats"}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}

              {activeTab === "orders" && (
                <div>
                  <div className="step-title">My Orders</div>
                  <MyOrders refreshToken={ordersRefreshToken} />
                </div>
              )}

              {activeTab === "vouchers" && (
                <div>
                  <MyVouchers emptyState="No vouchers yet — vouchers we send you will appear here with their QR code." />
                </div>
              )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <NotificationModal
        isOpen={notification.show}
        onClose={handleCloseNotification}
        message={notification.message}
      />
    </div>
  );
};

export default ProfilePage;